<script setup>
// One set of the ramp, in one of two sizes.
//
//   current   the set you are on: the weight in the serif, the instruction in
//             plates you can see (what comes off, what goes on, each side),
//             and the bar drawn to scale with only the changes called out.
//   upcoming  one quiet line: weight, reps, what changes, and a tag for the
//             last warm-up and the top set.
//
// Done sets are not drawn at all — RampPage folds them into its "done" line.
// The page owns the actions (Next set, Film) because they sit under Up next.
import { computed, ref } from 'vue'
import BarSleeve from './BarSleeve.vue'
import { fmt, repsText, rangeTo } from '../lib/loading.js'

const props = defineProps({
  set: { type: Object, required: true }, // from buildRamp: { total, reps, repsTo, isTop, isLastWarmUp, stack, added, removed }
  state: { type: String, required: true }, // 'current' | 'upcoming' | 'done'
  kit: { type: Object, required: true },
  plates: { type: Array, required: true },
  filmed: Boolean,
  // Animate the bar from the last set's loading to this one's (Kit's "Animate
  // loading", and never under Reduce Motion). Only the set you are on draws a bar.
  animate: Boolean,
})

const tag = computed(() => (props.set.isTop ? 'top set' : props.set.isLastWarmUp ? 'last warm-up' : ''))

// "× 1–2": the range, not "reps", beside a unit.
const perSet = computed(() => {
  const to = rangeTo(props.set.reps, props.set.repsTo)
  return to ? `${props.set.reps}–${to}` : String(props.set.reps)
})

// Nothing came off and every plate on the bar is new: that is loading an
// empty bar, and "Load" says so where "Add" would imply something was there.
const loading = computed(() => !props.set.removed.length && props.set.added.length === props.set.stack.length)

// THE CHANGE, SAID SHORT, for an Up next line: "off 10 · add 15".
const list = (xs) => [...new Set(xs)]
  .map((w) => { const n = xs.filter((x) => x === w).length; return n > 1 ? `${fmt(w)} ×${n}` : fmt(w) })
  .join(', ')
// In PARTS, each unbreakable, so a line too long for the row breaks between
// "off …" and "add …" and never inside a list of plates ("add 25," / "2.5").
const change = computed(() => {
  const { stack, added, removed } = props.set
  if (!stack.length) return ['empty bar']
  const parts = [
    removed.length && `off ${list(removed)}`,
    added.length && `${loading.value ? 'load' : 'add'} ${list(added)}`,
  ].filter(Boolean)
  return parts.map((p, i) => (i < parts.length - 1 ? `${p} ·` : p))
})

// THE PLATE CHIPS take their colour from the KIT RECORD, never from here — a
// lifter with black 25s gets black chips. The number on the chip is whichever
// of ink and white reads better on that colour (WCAG contrast, measured, not
// guessed from the weight), and a white or silver chip gets a hairline on the
// light ground, a black one a rim on the dark: BarSleeve's edge rules.
const recordFor = (w) => props.plates.find((p) => Number(p.weight) === Number(w)) || {}
function luminance(hex) {
  const m = (hex || '').replace('#', '').match(/../g)
  if (!m || m.length < 3) return null
  const lin = m.slice(0, 3).map((h) => parseInt(h, 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4))
  return { lum: 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2], rgb: m.slice(0, 3).map((h) => parseInt(h, 16) / 255) }
}
const INK_LUM = 0.0103 // #1A1B1C
function chip(w) {
  const colour = recordFor(w).colour || null
  const l = luminance(colour)
  if (!l) return { w, style: {}, cls: 'pale' }
  const onInk = (l.lum + 0.05) / (INK_LUM + 0.05)
  const onWhite = 1.05 / (l.lum + 0.05)
  const chroma = Math.max(...l.rgb) - Math.min(...l.rgb)
  return {
    w,
    style: { background: colour, color: onInk > onWhite ? '#1A1B1C' : '#FFFFFF' },
    cls: { pale: l.lum > 0.3 && chroma < 0.2, rim: l.lum < 0.05, small: fmt(w).length > 2 },
  }
}
const addChips = computed(() => props.set.added.map(chip))

const full = ref(false)
</script>

<template>
  <section
    v-if="state === 'current'"
    class="now"
    aria-current="step"
  >
    <p class="weight-line">
      <span class="w">{{ fmt(set.total) }}</span>
      <span class="per">{{ kit.unit }} × {{ perSet }}<template v-if="filmed"> · filmed</template></span>
      <span v-if="tag" class="tag">{{ tag }}</span>
    </p>

    <!-- The instruction, in plates. Coming off is a dashed ghost (the gap a
         plate leaves), going on is the plate itself in the kit's colour. -->
    <p class="do">
      <template v-if="!set.stack.length">
        <span>Empty bar</span>
      </template>
      <template v-else>
        <template v-if="set.removed.length">
          <span class="dim">Take off</span>
          <span
            v-for="(w, i) in set.removed" :key="`o${i}`"
            class="chip ghost" :class="{ small: fmt(w).length > 2 }"
          >{{ fmt(w) }}</span>
        </template>
        <template v-if="set.added.length">
          <span :class="{ after: set.removed.length }">{{ loading ? 'Load' : 'Add' }}</span>
          <span
            v-for="(c, i) in addChips" :key="`a${i}`"
            class="chip" :class="c.cls" :style="c.style"
          >{{ fmt(c.w) }}</span>
        </template>
        <span class="each">each side</span>
      </template>
    </p>

    <BarSleeve
      class="drawing"
      :stack="set.stack" :added="set.added" :removed="set.removed" :plates="plates"
      :unit="kit.unit" :collar-weight="kit.collarWeight" :animate="animate"
      @full="full = $event"
    />
    <p v-if="full" class="full">Sleeve's full. Check this fits your bar.</p>
  </section>

  <li v-else-if="state === 'upcoming'" class="row">
    <span class="rw">{{ fmt(set.total) }}</span>
    <span class="rr">{{ repsText(set.reps, set.repsTo) }}</span>
    <span class="rc"><span v-for="(p, i) in change" :key="i" class="part">{{ p }}</span></span>
    <span v-if="tag" class="rt">{{ tag }}</span>
  </li>
</template>

<style scoped>
/* Values are screens.py `ramp()`'s. */
.now { display: flex; flex-direction: column; padding-top: 22px; }

.weight-line { display: flex; align-items: baseline; gap: 10px; margin: 0; flex-wrap: wrap; }
/* Bounded by height as well as width: on a 667px phone a fixed 64px readout is
   what pushes the drawing under the button. */
.w {
  font: 400 min(4rem, 17vw, 8.5vh)/1 var(--face-serif); letter-spacing: -0.01em;
  color: var(--ink); font-variant-numeric: tabular-nums;
}
.per { font: 400 0.9375rem/1 var(--face-mono); color: var(--dim); }
.tag, .each, .rt { font-family: var(--face-serif); font-style: italic; font-weight: 400; color: var(--dim); }
.tag { margin-left: auto; font-size: 1rem; }

.do {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px 10px;
  margin: 12px 0 0; min-height: 30px;
  font: 400 0.9375rem/1.2 var(--face-mono); color: var(--ink);
}
.do .after { margin-left: 6px; }
.each { margin-left: auto; font-size: 1rem; }

.chip {
  box-sizing: border-box; flex: none;
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 50%;
  font: 600 0.6875rem/1 var(--face-mono); font-variant-numeric: tabular-nums;
}
.chip.small { font-size: 0.5625rem; }
.chip.ghost { border: 1.5px dashed var(--faint); color: var(--dim); background: transparent; }
.chip.pale { box-shadow: inset 0 0 0 1px var(--pale-edge, #8A9197); }
.chip.rim { box-shadow: inset 0 0 0 1px var(--dark-rim, transparent); }

/* The bar runs in from the screen edge: bleed by the page's padding. 72vw so
   a dashed "off" plate still fits past the sleeve tip. */
.drawing { --bleed: var(--wu-pad); --sleeve-span: min(72vw, 330px); margin-top: 6px; }
.full { margin: 0 0 4px; font: 400 0.75rem/1.4 var(--face-mono); color: var(--dim); }

/* ---- Up next ---- */
.row {
  display: grid; grid-template-columns: 52px 64px minmax(0, 1fr) auto;
  align-items: baseline; gap: 8px;
  box-sizing: border-box; min-height: 34px; padding: 8px 0 6px;
  border-top: 1px solid var(--line);
  list-style: none;
}
.rw { font: 500 0.9375rem/1.2 var(--face-mono); color: var(--ink); font-variant-numeric: tabular-nums; }
.rr, .rc { font: 400 0.75rem/1.3 var(--face-mono); color: var(--dim); }
.rc { display: flex; flex-wrap: wrap; column-gap: 0.6em; }
.part { white-space: nowrap; }
.rt { font-size: 0.9375rem; white-space: nowrap; }
</style>
